import React from "react";
import styled from "styled-components";
import { Text, Flex } from "rebass/styled-components";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useHistory } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { useFeatureGate } from "@statsig/react-bindings";

import theme from "theme/themeThree";
import { ESupportedGames } from "MVVM/@types";
import { analytics } from "MVVM/Analytics";
import { Button } from "MVVM/Components/Button";
import { useLinkedAccounts } from "MVVM/Hooks/useLinkedAccounts";
import { useUser } from "MVVM/Hooks/useUser";
import { MatchHistoryGame } from "MVVM/State/matchHistory";
import { StudioIcon } from "MVVM/Views/Studio/Components/StudioIcons";
import { URLS } from "MVVM/Utilities/Maps";
import {
  StyledSteamIcon,
  StyledEpicIcon,
  StyledFaceitIcon,
  StyledRiotIcon,
  StyledBattleNetIcon,
} from "MVVM/Views/MatchHistory/styledComponents";
import { EStudioIconType } from "MVVM/Views/Studio/@types";

export const LinkingOptions = () => {
  const showValorant = useFeatureGate("show_valorant").value;
  const {
    hasRiotLinked,
    hasSteamLinked,
    hasFaceitLinked,
    hasEpicLinked,
    hasBattlenetLinked,
  } = useLinkedAccounts();
  const { t } = useTranslation();
  const matchHistoryGame = useRecoilValue(MatchHistoryGame);
  const valveOrFaceitGameSelected =
    matchHistoryGame === ESupportedGames.CS2 ||
    matchHistoryGame === ESupportedGames.DOTA2 ||
    matchHistoryGame === ESupportedGames.NONE;
  const riotGameSelected =
    matchHistoryGame === ESupportedGames.LOL ||
    matchHistoryGame === ESupportedGames.VALORANT ||
    matchHistoryGame === ESupportedGames.NONE;
  const epicGameSelected =
    matchHistoryGame === ESupportedGames.FORTNITE ||
    matchHistoryGame === ESupportedGames.NONE;
  const battlenetGameSelected =
    matchHistoryGame === ESupportedGames.OW2 ||
    matchHistoryGame === ESupportedGames.NONE;

  return (
    <Flex flexDirection="column" style={{ gap: 16 }}>
      {!hasSteamLinked && valveOrFaceitGameSelected && (
        <LinkAccountCard
          button={<LinkSteam />}
          game={[EStudioIconType.CS2, EStudioIconType.Dota2]}
          text={
            <LinkAccountCardText
              platform="Steam Account"
              games={["Counter-Strike 2", "Dota 2"]}
            />
          }
        />
      )}
      {!hasFaceitLinked &&
        valveOrFaceitGameSelected &&
        ESupportedGames.DOTA2 !== matchHistoryGame && (
          <LinkAccountCard
            button={<LinkFaceit />}
            game={[EStudioIconType.CS2]}
            text={
              <LinkAccountCardText
                platform="FACEIT Account"
                games={["Counter-Strike 2"]}
              />
            }
          />
        )}
      {!hasRiotLinked && riotGameSelected && (
        <LinkAccountCard
          button={<LinkRiot />}
          game={
            showValorant
              ? [EStudioIconType.LoL, EStudioIconType.Valorant]
              : [EStudioIconType.LoL]
          }
          text={
            <LinkAccountCardText
              platform="Riot Account"
              games={
                showValorant
                  ? ["League of Legends", "Valorant"]
                  : ["League of Legends"]
              }
            />
          }
        />
      )}
      {!hasEpicLinked && epicGameSelected && (
        <LinkAccountCard
          button={<LinkEpic />}
          game={[EStudioIconType.Fortnite]}
          text={
            <LinkAccountCardText
              platform="Epic Games Account"
              games={["Fortnite"]}
            />
          }
        />
      )}
      {!hasBattlenetLinked && battlenetGameSelected && (
        <LinkAccountCard
          button={<LinkBattlenet />}
          game={[EStudioIconType.OW2]}
          text={
            <LinkAccountCardText
              platform="Battle.net Account"
              games={["Overwatch 2"]}
            />
          }
        />
      )}
    </Flex>
  );
};

const Games = ({ gameString }: { gameString: string }) => (
  <span style={{ color: theme.colors.ice, fontWeight: 500 }}>{gameString}</span>
);

const LinkAccountCardText = ({
  platform,
  games,
}: {
  platform: string;
  games: string[];
}) => {
  const getGameString = () => games.join(" and ");
  const gameString = getGameString();

  return (
    <Trans
      i18nKey="linkingOptions.connectPlatformForGame"
      defaults="Connect your <bold>{{platform}}</bold> to access your <games>{{gameString}}</games> matches."
      values={{ platform, game: gameString }}
      components={{
        bold: <span style={{ fontWeight: "bold" }} />,
        games: <Games gameString={gameString} />,
      }}
    />
  );
};

const LinkAccountCard = ({
  button,
  game,
  text,
}: {
  button: React.ReactNode;
  game: EStudioIconType[];
  text: React.ReactNode;
}) => {
  return (
    <StyledLinkAccountCard>
      <Flex style={{ gap: 6 }} flexGrow="1" flexDirection={"column"}>
        <Flex flexDirection={"row"} style={{ gap: "6px" }}>
          {game.map((g) => (
            <StudioIcon type={g} size={40} styles={{ flexShrink: 0 }} />
          ))}
        </Flex>
        <Flex flexDirection="column" style={{ textAlign: "left", gap: 6 }}>
          <Text fontSize="2" color="chalk">
            {text}
          </Text>
        </Flex>
      </Flex>

      {button}
    </StyledLinkAccountCard>
  );
};

export const LinkFaceit = () => {
  const setMatchHistoryGame = useSetRecoilState(MatchHistoryGame);
  const history = useHistory();
  const { allstarUser } = useUser();

  return (
    <LinkFaceitButton
      size="small"
      onClick={() => {
        analytics.track("Match History - Button Click", {
          type: "Link Faceit",
        });
        setMatchHistoryGame(ESupportedGames.CS2);
        history.push(
          `${URLS.AUTH_ROUTES.FACEIT}?flow=${
            allstarUser.loggedIn ? "link" : "signup"
          }&referrer=CS2_MATCH_HISTORY`,
        );
      }}
      flex={true}
    >
      <Flex justifyContent="center" alignItems="center">
        <StyledFaceitIcon style={{ marginTop: "-4px" }} />
        <Text>Link FACEIT</Text>
      </Flex>
    </LinkFaceitButton>
  );
};

export const LinkSteam = () => {
  const setMatchHistoryGame = useSetRecoilState(MatchHistoryGame);
  const history = useHistory();
  const { allstarUser } = useUser();

  return (
    <LinkSteamButton
      size="small"
      onClick={() => {
        analytics.track("Match History - Button Click", {
          type: "Link Steam",
        });
        setMatchHistoryGame(ESupportedGames.CS2);
        history.push(
          `${URLS.AUTH_ROUTES.STEAM}?flow=${
            allstarUser.loggedIn ? "link" : "signup"
          }&referrer=CS2_MATCH_HISTORY`,
        );
      }}
      flex={true}
    >
      <Flex justifyContent="center" alignItems="center">
        <StyledSteamIcon />
        <Text>Link Steam</Text>
      </Flex>
    </LinkSteamButton>
  );
};

export const LinkRiot = () => {
  const setMatchHistoryGame = useSetRecoilState(MatchHistoryGame);
  const history = useHistory();
  const { allstarUser } = useUser();

  return (
    <LinkRiotButton
      size="small"
      onClick={() => {
        analytics.track("Match History - Button Click", {
          type: "Link Riot",
        });
        setMatchHistoryGame(ESupportedGames.LOL);
        history.push(
          `${URLS.AUTH_ROUTES.RIOT}?flow=${
            allstarUser.loggedIn ? "link" : "signup"
          }&referrer=LEAGUE_MATCH_HISTORY`,
        );
      }}
      flex={true}
    >
      <Flex justifyContent="center" alignItems="center">
        <StyledRiotIcon />
        <Text>Link Riot ID</Text>
      </Flex>
    </LinkRiotButton>
  );
};
export const LinkEpic = () => {
  const setMatchHistoryGame = useSetRecoilState(MatchHistoryGame);
  const history = useHistory();
  const { allstarUser } = useUser();

  return (
    <LinkEpicButton
      size="small"
      onClick={() => {
        analytics.track("Match History - Button Click", {
          type: "Link Epic",
        });
        setMatchHistoryGame(ESupportedGames.FORTNITE);
        history.push(
          `${URLS.AUTH_ROUTES.EPIC}?flow=${
            allstarUser.loggedIn ? "link" : "signup"
          }&referrer=FORTNITE_MATCH_HISTORY`,
        );
      }}
      flex={true}
    >
      <Flex justifyContent="center" alignItems="center">
        <StyledEpicIcon />
        <Text>Link Epic</Text>
      </Flex>
    </LinkEpicButton>
  );
};
const LinkBattlenet = () => {
  const setMatchHistoryGame = useSetRecoilState(MatchHistoryGame);
  const history = useHistory();
  const { allstarUser } = useUser();

  return (
    <LinkBattlenetButton
      size="small"
      onClick={() => {
        analytics.track("Match History - Button Click", {
          type: "Link Battlenet",
        });
        setMatchHistoryGame(ESupportedGames.OW2);
        history.push(
          `${URLS.AUTH_ROUTES.BATTLENET}?flow=${
            allstarUser.loggedIn ? "link" : "signup"
          }&referrer=FORTNITE_MATCH_HISTORY`,
        );
      }}
      flex={true}
    >
      <Flex justifyContent="center" alignItems="center">
        <StyledBattleNetIcon />
        <Text>Link Battle.net</Text>
      </Flex>
    </LinkBattlenetButton>
  );
};
const LinkButton = styled(Button)`
  border-radius: 8px;
  color: var(--Ice-100, var(--colors-ice-500100, #f7fcfd));
  min-width: 160px;
`;

const LinkFaceitButton = styled(LinkButton)`
  background-color: #ff5500;
  &:hover {
    background-color: #cc4400;
  }
`;

const LinkSteamButton = styled(LinkButton)`
  background-color: #1c2a34;
  &:hover {
    background-color: #1b2838;
  }
`;

const LinkRiotButton = styled(LinkButton)`
  background-color: #d13639;
  &:hover {
    background-color: #b5272a;
  }
`;

const LinkEpicButton = styled(LinkButton)`
  background-color: #000000;
  &:hover {
    background-color: #000000;
  }
`;

const LinkBattlenetButton = styled(LinkButton)`
  background: #0c1318;
  &:hover {
    background: #0c1318;
  }
`;

const StyledLinkAccountCard = styled(Flex)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.navy};
  border-radius: 12px;
  gap: 20px;
  padding: 24px 24px;
  flex-wrap: wrap;
`;
